/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { graphql, type PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { AIFeedbackPropertiesProvider } from '@atlassian/ai-components/feedback-properties-provider';

import { AISnippetContent } from '../../../common/ui';
import { type aiSnippetLoaderQuery } from './__generated__/aiSnippetLoaderQuery.graphql';

const SNIPPET_ENTRYPOINT_ID = '55dba3ae-58fa-41e0-828b-1e30fe66c0ba';

type Props = {
	contentId: string;
	fallbackText?: string;
	isHidden?: boolean;
	maxLines: number;
	queryRef: PreloadedQuery<aiSnippetLoaderQuery>;
	showAwareness?: boolean;
	showFooter?: boolean;
	source?: string;
};

export const AISnippetLoader = ({
	contentId,
	fallbackText,
	queryRef,
	source,
	isHidden,
	maxLines,
	showFooter,
	showAwareness,
}: Props): JSX.Element => {
	const data = usePreloadedQuery<aiSnippetLoaderQuery>(
		graphql`
			query aiSnippetLoaderQuery(
				$contentId: ID!
				$contentType: KnowledgeGraphContentType!
				$cloudId: String!
				$includeMe: Boolean!
			) {
				confluence_latestKnowledgeGraphObjectV2(
					contentId: $contentId
					contentType: $contentType
					cloudId: $cloudId
					objectType: snippet_v2
				) {
					__typename
					objectData
				}
				me @include(if: $includeMe) {
					user {
						accountId
						name
					}
				}
			}
		`,
		queryRef,
	);

	const aiSnippetText = data?.confluence_latestKnowledgeGraphObjectV2?.objectData;

	const content = (
		<AISnippetContent
			text={aiSnippetText || fallbackText}
			contentId={contentId}
			showFooter={!!aiSnippetText && showFooter}
			showAwareness={!!aiSnippetText && showAwareness}
			source={source}
			isHidden={isHidden}
			maxLines={maxLines}
			renderAsMarkdown={!!aiSnippetText}
		/>
	);

	if (fg('jira-cc-ai-snippet-gate')) {
		return (
			<AIFeedbackPropertiesProvider
				entrypointId={SNIPPET_ENTRYPOINT_ID}
				userId={data?.me?.user?.accountId}
				displayName={data?.me?.user?.name}
			>
				{content}
			</AIFeedbackPropertiesProvider>
		);
	}

	return content;
};
